import { environment } from '@env/environment';
import { OVERRIDE_LIST_KEY, OVERRIDE_TOGGLE_KEY } from 'shared/constants/general-constants';
import { FeatureFlagEnum, FeatureFlags } from 'shared/interfaces/environment.interface';

export const ensureOverrideListExists = (): void => {
    const overrideToggleValue = localStorage.getItem(OVERRIDE_TOGGLE_KEY);
    if (overrideToggleValue !== 'true') return;

    const storedOverrideList = localStorage.getItem(OVERRIDE_LIST_KEY);
    if (!storedOverrideList) {
        const blankOverrideList: Record<FeatureFlagEnum, null> = {} as any;
        Object.values(FeatureFlagEnum).forEach((featureFlagKey) => {
            blankOverrideList[featureFlagKey] = null;
        });
        localStorage.setItem(OVERRIDE_LIST_KEY, JSON.stringify(blankOverrideList));
        return;
    }

    const overrideList: Record<string, boolean | null> = JSON.parse(storedOverrideList);
    const definedFlagKeys = Object.values(FeatureFlagEnum);
    let listChanged = false;

    Object.keys(overrideList).forEach((storedKey) => {
        if (!definedFlagKeys.includes(storedKey as FeatureFlagEnum)) {
            delete overrideList[storedKey];
            listChanged = true;
        }
    });

    definedFlagKeys.forEach((featureFlagKey) => {
        if (!(featureFlagKey in overrideList)) {
            overrideList[featureFlagKey] = null;
            listChanged = true;
        }
    });

    if (listChanged) {
        localStorage.setItem(OVERRIDE_LIST_KEY, JSON.stringify(overrideList));
    }
};

export const readOverrideList = (): Partial<Record<FeatureFlagEnum, boolean | null>> => {
    const raw = localStorage.getItem(OVERRIDE_LIST_KEY);
    return raw ? (JSON.parse(raw) as any) : {};
};

export const getMergedFeatureFlags = (): FeatureFlags => {
    ensureOverrideListExists();

    const baseFeatureFlags = environment.featureFlags as FeatureFlags;
    const overrideToggleValue = localStorage.getItem(OVERRIDE_TOGGLE_KEY);
    if (overrideToggleValue !== 'true') return { ...baseFeatureFlags };

    const overrideValues = readOverrideList();
    const mergedFlags: FeatureFlags = { ...baseFeatureFlags };

    Object.values(FeatureFlagEnum).forEach((featureFlagKey) => {
        const overrideValue = overrideValues[featureFlagKey];
        if (overrideValue === true || overrideValue === false) {
            mergedFlags[featureFlagKey] = overrideValue;
        }
    });

    return mergedFlags;
};

export const getFeatureFlag = (flag: FeatureFlagEnum): boolean => getMergedFeatureFlags()[flag];
